import React, { useState, useCallback } from 'react';
import { Code2, X, Copy, Download, Check } from 'lucide-react';
import { FormField, FormSchema } from '../types/form';
import { generateFormJSON, FormJSONOutput } from '../utils/generateFormJSON';

interface ShowJsonButtonProps {
  schema: Pick<FormSchema, 'title' | 'handler' | 'fields'> & {
    title: string;
    handler: string;
    fields: FormField[];
  };
  formValues?: Record<string, any>;
  /** Optional label override */
  label?: string;
}

export const ShowJsonButton: React.FC<ShowJsonButtonProps> = ({
  schema,
  formValues = {},
  label = 'Show JSON',
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [jsonData, setJsonData] = useState<FormJSONOutput | null>(null);

  const handleOpen = useCallback(() => {
    setJsonData(generateFormJSON(schema, formValues));
    setOpen(true);
  }, [schema, formValues]);

  const jsonString = jsonData ? JSON.stringify(jsonData, null, 2) : '';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${schema.handler || 'form'}-schema.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={schema.fields.length === 0}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all flex items-center gap-2 disabled:opacity-50 text-sm"
      >
        <Code2 className="w-4 h-4" />
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div className="bg-gray-900 text-gray-100 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Code2 className="w-5 h-5 text-blue-400" />
                <span className="font-semibold text-white">Form JSON</span>
                <span className="text-xs text-gray-400 ml-1">
                  {jsonData?.formTitle}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-md hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* JSON content */}
            <div className="overflow-auto flex-1 p-5">
              <pre className="text-xs leading-relaxed font-mono whitespace-pre text-gray-200">
                {jsonString}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
